import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Space, Button, Spin, Alert, Tag } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconAlertTriangle, IconRefresh, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getAdminBookingLines, retryAdminBookingLine, resolveAdminBookingLine, IAdminBookingLine } from '../../../services/AdminService';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;

const PENDING_RESOLUTION_STATUS = 'PendingResolution';

/**
 * Admin panel's view over BookingLine rows a separate Stripe Transfer
 * couldn't reach automatically (see BookingLineTransferService's own doc
 * comment) -- money the base charge already collected but that's stuck
 * short of its real payee. "Reintentar" replays the exact same Transfer
 * attempt AdminFinanceController.RetryBookingLine calls into (same
 * idempotency key, so it can never double-pay), right here, synchronously.
 * "Marcar como resuelto" is for when the admin settled the payee some other
 * way (e.g. a manual bank transfer) -- requires a reason, recorded in the
 * audit log, and moves the line to ManuallyResolved so the automatic sweep
 * never touches it again.
 */
const BookingLineResolutionSection: React.FC = () => {
  const { t } = useTranslation();
  const [lines, setLines] = useState<IAdminBookingLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryingId, setRetryingId] = useState<string | undefined>(undefined);
  const [resolveTargetId, setResolveTargetId] = useState<string | undefined>(undefined);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminBookingLines(PENDING_RESOLUTION_STATUS)
      .then(setLines)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = async (line: IAdminBookingLine) => {
    setRetryingId(line.id);
    try {
      const refreshed = await retryAdminBookingLine(line.id);
      if (refreshed.paymentStatus === PENDING_RESOLUTION_STATUS) {
        message.error(translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_RETRY_STILL_FAILING));
      } else {
        message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      }
      load();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setRetryingId(undefined);
    }
  };

  const handleResolve = async (reason: string) => {
    if (!resolveTargetId) return;
    try {
      await resolveAdminBookingLine(resolveTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setResolveTargetId(undefined);
      load();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  // Shared by the desktop actions column and the mobile card footer.
  const renderActions = (record: IAdminBookingLine) => (
    <Space wrap>
      <Button size="small" icon={<IconRefresh size={14} />} loading={retryingId === record.id} onClick={() => handleRetry(record)}>
        {translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_RETRY_BUTTON)}
      </Button>
      <Button size="small" icon={<IconCheck size={14} />} onClick={() => setResolveTargetId(record.id)}>
        {translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_RESOLVE_BUTTON)}
      </Button>
    </Space>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_BOOKING_LINES)}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_DESCRIPTION)}
      </Text>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_LOAD_ERROR)} />
      ) : lines.length === 0 ? (
        <EmptyState
          icon={<IconAlertTriangle size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminBookingLine>
          rowKey="id"
          dataSource={lines}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: String(record.id),
            title: record.title,
            subtitle: record.providerName,
            badge: <Tag style={{ marginInlineEnd: 0 }}>{record.lineType}</Tag>,
            fields: [
              {
                label: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_AMOUNT),
                value: `${record.amount.toFixed(2)} ${record.hostCurrencyCode}`,
              },
              { label: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_ATTEMPTS), value: record.transferAttempts },
              {
                label: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_LAST_ATTEMPT),
                value: record.lastTransferAttemptAt ? new Date(record.lastTransferAttemptAt).toLocaleString() : '—',
                wide: true,
              },
              // Why the transfer failed is what decides between retrying and
              // resolving by hand, so it never gets truncated into a column.
              {
                label: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_ERROR),
                value: record.transferLastErrorMessage ?? '—',
                wide: true,
              },
            ],
            actions: renderActions(record),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_TITLE),
              dataIndex: 'title',
              key: 'title',
              sorter: textSorter<IAdminBookingLine>('title'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_TYPE),
              dataIndex: 'lineType',
              key: 'lineType',
              render: (value: string) => <Tag>{value}</Tag>,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_PROVIDER),
              dataIndex: 'providerName',
              key: 'providerName',
              sorter: textSorter<IAdminBookingLine>('providerName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_AMOUNT),
              dataIndex: 'amount',
              key: 'amount',
              sorter: numberSorter<IAdminBookingLine>('amount'),
              render: (value: number, record: IAdminBookingLine) => `${value.toFixed(2)} ${record.hostCurrencyCode}`,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_ATTEMPTS),
              dataIndex: 'transferAttempts',
              key: 'transferAttempts',
              sorter: numberSorter<IAdminBookingLine>('transferAttempts'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_ERROR),
              dataIndex: 'transferLastErrorMessage',
              key: 'transferLastErrorMessage',
              render: (value?: string) => value ?? '—',
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_COL_LAST_ATTEMPT),
              dataIndex: 'lastTransferAttemptAt',
              key: 'lastTransferAttemptAt',
              sorter: dateSorter<IAdminBookingLine>('lastTransferAttemptAt'),
              defaultSortOrder: 'descend',
              render: (value?: string) => (value ? new Date(value).toLocaleString() : '—'),
            },
            {
              title: '',
              key: 'actions',
              render: (_: unknown, record: IAdminBookingLine) => renderActions(record),
            },
          ]}
        />
      )}

      <AdminReasonModal
        open={resolveTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_RESOLVE_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_BOOKING_LINES_RESOLVE_BUTTON)}
        onCancel={() => setResolveTargetId(undefined)}
        onConfirm={handleResolve}
      />
    </div>
  );
};

export default BookingLineResolutionSection;
